import { useState, type ReactNode } from "react";
import { Loader2, MapPin, Zap } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { InlineEdit } from "@/components/InlineEdit";
import { AddContactModal } from "@/components/add/AddContactModal";
import { ActivityTimeline } from "@/components/activity/ActivityTimeline";
import { EntityRemindersButton } from "@/components/reminders/EntityRemindersButton";
import { useCurrentUserTimezone } from "@/hooks/useCurrentUserTimezone";
import { useCompanyField } from "@/hooks/useCompanyField";
import { useCompanySummary } from "@/hooks/useRecordSummary";
import { RecordFrame, RecordPanel } from "@/components/record/RecordFrame";
import { RecordHeader, RecordChip, type SublineItem } from "@/components/record/RecordHeader";
import { FactsStrip } from "@/components/record/FactsStrip";
import { ActionBar } from "@/components/record/ActionBar";
import { BriefPanel } from "@/components/record/BriefPanel";
import { EditableTextPanel } from "@/components/record/EditableTextPanel";
import { CompanyRail } from "@/components/record/CompanyRail";
import { CompanyReach } from "@/components/record/CompanyReach";
import { companyLinkItems } from "@/components/record/LinkField";
import { CompanyNameEdit } from "@/components/record/CompanyNameEdit";
import { CompanyMoreMenu } from "@/components/record/CompanyMoreMenu";
import { ArchiveCompanyDialog } from "@/components/record/ArchiveCompanyDialog";
import { useRecordCompose } from "@/components/record/useRecordCompose";
import { companyFacts } from "@/components/record/facts/companyFacts";
import { fullDay, localYmd, primaryContact, sourceLabel, websiteHost } from "@/components/record/recordFormat";
import type { CompanyData } from "./companyTypes";

/** A firm with no page of its own: today the COI category, the bucket for firms that are none of the other four. */
export default function PlainCompanyRecord({ company }: { company: CompanyData }) {
  const queryClient = useQueryClient();
  const userTz = useCurrentUserTimezone();
  const { saveFieldAsync } = useCompanyField(company.id);
  const summary = useCompanySummary(company.id);
  const { compose, setCompose, openLog, nextStepOpen, setNextStepOpen } = useRecordCompose();
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [enrichPhase, setEnrichPhase] = useState<"idle" | "apollo">("idle");
  const id = company.id;
  const domain = websiteHost(company.website) || company.enrichmentData?.domain;

  const runSimpleEnrich = async () => {
    if (!domain) { toast.error("No website to enrich from"); return; }
    setEnrichPhase("apollo");
    try {
      const res = await fetch(`/api/companies/${id}/enrich-combined`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain }),
      });
      const data = await res.json();
      // Auto-apply Apollo data for simple entities
      if (data.apollo) {
        const fields: Record<string, any> = {};
        if (data.apollo.industry && !company.industry) fields.industry = data.apollo.industry;
        if (data.apollo.headquarters && !company.headquarters) fields.headquarters = [data.apollo.city, data.apollo.state].filter(Boolean).join(", ");
        if (data.apollo.employeeCount && !company.employeeCount) fields.employeeCount = String(data.apollo.employeeCount);
        if (data.apollo.estimatedRevenue && !company.revenueRange) fields.revenueRange = data.apollo.estimatedRevenue;
        if (data.apollo.description && !company.description) fields.description = data.apollo.description;
        if (data.apollo.linkedinUrl && !company.linkedinUrl) fields.linkedinUrl = data.apollo.linkedinUrl;
        // Always update enrichment metadata so the "enriched" indicator shows
        fields.enrichmentData = { ...(company.enrichmentData || {}), source: "apollo", enrichedAt: new Date().toISOString(), apolloId: data.apollo.apolloId || null, phone: data.apollo.phone || (company.enrichmentData as any)?.phone || null };
        if (Object.keys(fields).length > 0) {
          await fetch(`/api/companies/${id}`, { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(fields) });
          queryClient.invalidateQueries({ queryKey: ["company", id] });
          toast.success(`Enriched ${Object.keys(fields).length} fields from Apollo`);
        } else {
          toast("No new data found from Apollo");
        }
      } else {
        toast.error("Apollo enrichment returned no data");
      }
    } catch { toast.error("Enrichment failed"); }
    finally { setEnrichPhase("idle"); }
  };

  const people = company.contacts ?? [];
  const primary = primaryContact(people);
  const today = localYmd(new Date());

  const subline: SublineItem[] = [
    { content: <InlineEdit label="Industry" value={company.industry} placeholder="Industry" addLabel="Add industry" onSave={(v) => saveFieldAsync("industry", v.trim() || null)} /> },
    { icon: <MapPin />, content: <InlineEdit label="Location" value={company.headquarters} placeholder="City, State" addLabel="Add location" onSave={(v) => saveFieldAsync("headquarters", v.trim() || null)} /> },
    ...companyLinkItems({ website: company.website, linkedinUrl: company.linkedinUrl, onSave: (field, v) => saveFieldAsync(field, v) }),
  ];

  const header = (
    <RecordHeader
      crumb={{ label: "All Contacts", href: "/contacts" }}
      current={company.name}
      name={<CompanyNameEdit value={company.name} onSave={(t) => saveFieldAsync("name", t)} />}
      chips={<>
        <RecordChip tone="category">{company.category === "COI" ? "Company" : company.category}</RecordChip>
        {people.some((p) => p.engaged) && <RecordChip tone="good">Spoken with</RecordChip>}
      </>}
      subline={subline}
      reach={<CompanyReach people={people} mainPhone={company.enrichmentData?.phone} />}
      actions={
        <ActionBar onLog={openLog} email={primary?.email} onNextStep={() => setNextStepOpen(true)}>
          <EntityRemindersButton entityType="company" entityId={company.id} entityName={company.name} userTimezone={userTz} />
          <Button size="sm" variant="outline" className="gap-1.5" onClick={runSimpleEnrich} disabled={enrichPhase === "apollo" || !domain} title={domain ? "Fill blanks from Apollo" : "Add a website to enrich from it"}>
            {enrichPhase === "apollo" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
            {enrichPhase === "apollo" ? "Enriching…" : "Enrich"}
          </Button>
          <CompanyMoreMenu company={company} onArchive={() => setArchiveOpen(true)} />
        </ActionBar>
      }
    />
  );

  const work = (
    <>
      <BriefPanel
        summary={summary.data}
        loading={summary.isLoading}
        error={summary.isError}
        onRetry={() => summary.refetch()}
        openLabel={`Open on ${company.name}`}
        target={{ companyId: company.id }}
        nextStepOpen={nextStepOpen}
        onNextStepOpenChange={setNextStepOpen}
      />
      <EditableTextPanel
        title="About"
        value={company.description}
        onSave={(v) => saveFieldAsync("description", v.trim() || null)}
        emptyText="No description yet. Enrich to pull it from their website, or add it here."
      />
      <RecordPanel id="record-activity">
        <ActivityTimeline
          scope={{ kind: "company", companyId: company.id, companyName: company.name, contacts: people }}
          compose={compose}
          onComposeChange={setCompose}
        />
      </RecordPanel>
    </>
  );

  const details: [string, ReactNode][] = [
    ["Employees", <InlineEdit key="employees" label="Employees" value={company.employeeCount} placeholder="e.g. 85" addLabel="Add" onSave={(v) => saveFieldAsync("employeeCount", v.trim() || null)} />],
    ["Revenue", <InlineEdit key="revenue" label="Revenue" value={company.revenueRange} placeholder="e.g. $10M" addLabel="Add" onSave={(v) => saveFieldAsync("revenueRange", v.trim() || null)} />],
    ["Added", fullDay(company.createdAt)],
    ["Source", sourceLabel(company.creationSource)],
  ];

  return (
    <AppLayout title={company.name}>
      <RecordFrame
        header={header}
        facts={summary.data
          ? <FactsStrip facts={companyFacts(summary.data, today)} />
          : summary.isLoading ? <div className="h-[58px] animate-pulse rounded-lg border bg-card" /> : null}
        work={work}
        rail={
          <CompanyRail
            company={company}
            summary={summary}
            details={details}
            onSaveNotes={(v) => saveFieldAsync("notes", v.trim() || null)}
            onAddPerson={() => setAddPersonOpen(true)}
          />
        }
      />
      <AddContactModal open={addPersonOpen} onOpenChange={setAddPersonOpen} preselectedCompanyId={company.id} preselectedCompanyName={company.name} />
      <ArchiveCompanyDialog company={company} open={archiveOpen} onOpenChange={setArchiveOpen} />
    </AppLayout>
  );
}
